<template>

    <div class="msg">

        <div class="msg-avatar">
            <ChatAvatar :size="48" :role="senderRole"></ChatAvatar>
        </div>

        <div class="msg-container">

            <div class="msg-header">
                <h2 class="sender-name">{{ senderName }}</h2>
                <div class="timestamp">{{ sentTime }}</div> 
            </div>

            <div class="msg-wrapper">

                <div class="msg-content">
                    
                    <div v-if="isBotMessage" class="text" v-html="$t(sanitizedMessage)"></div>
                    
                    <div v-if="!isBotMessage" class="text">{{ sanitizedMessage }}</div>
                    
                    <EscalationOffer 
                        v-if="isEscalationOffered" 
                        :chatId="chatId"
                        :chatMessageId="chatMessageId" 
                        :escalationOption="getEscalationOption" 
                        :currentDecision="getEscalationDecision"
                        :updateDecisionCallback="updateEscalationDecision"
                    ></EscalationOffer>

                    
                </div>

                <RatingToolbar v-if="isMessageRateable" :callbackFunc="rateBotResponse" :currentRating="currentRating"></RatingToolbar>

            </div>

        </div>

    </div>

</template>
  
<script>
    import ChatAvatar from "@/components/chatavatar.vue"
    import RatingToolbar from "@/components/ratingtoolbar.vue"
    import EscalationOffer from "@/views/chat/partials/escalation/escalationoffer.vue"
    import DOMPurify from 'dompurify';
    import { mapActions } from "vuex";

    export default {
        name: 'ChatMessage',
        props: {
        message: {
            type: Object,
            required: true
            }
        },
        components: {
            ChatAvatar,
            RatingToolbar,
            EscalationOffer,
        },
        data() {
            return {
                messageId: this.message.chatMessageId,
                messageRating: this.message.rating,
                escalationOffered: this.message.escalationOffered,
                escalationOption: this.message.escalationOption,
                escalationDecision: this.message.escalationDecision,
            }
        },
        mounted() {
            if (this.escalationOffered && this.escalationDecision == "-") {
                this.setEscalationPending(true);
            }
        },
        methods: {
            ...mapActions([
                "rateResponse",
                "setEscalationPending",
            ]),
            async rateBotResponse(rating) {
                
                const payload = {
                    chatMessageId: this.message.chatMessageId,
                    rating: rating
                };
                
                const result = await this.rateResponse(payload);
                if (result) {
                    this.updateMessageRating(rating);
                }
            },
            formatBotMessage(message) {
                const boldPattern = /\*\*(.*?)\*\*/g;
                return message.replace(boldPattern, '<strong>$1</strong>');
            },
            updateMessageRating(rating) {
                this.messageRating = rating;
            },
            // escalation
            updateEscalationDecision(escalationDecision) {
                this.escalationDecision = escalationDecision
            },
        },
        computed: {
            chatId() {
                return this.message.chatId;
            },
            chatMessageId() {
                return this.messageId;
            },
            isBotMessage() {
                return this.message && this.message.role === "assistant";
            },
            isUserMessage() {
                return this.message && this.message.role === "user";
            },
            isMessageRateable() {
                // currently only support rating successful question answer attempts
                // (no support for rating non-pertinent/not enough info responses)
                if (this.message.role === "assistant" && this.message.rating != "" && this.message.escalationOffered == false) {
                    return true;
                }

                return false;
            },
            currentRating() {
                return this.messageRating;
            },
            isEscalationOffered() {
                return this.escalationOffered;
            },
            getEscalationOption() {
                return this.escalationOption;
            },
            getEscalationDecision() {
                return this.escalationDecision;
            },
            sanitizedMessage() {
                const sanitized = DOMPurify.sanitize(this.message.message, {
                    ADD_ATTR: ['target', 'rel']
                });

                if (this.isBotMessage) {
                    return this.formatBotMessage(sanitized);
                }

                return sanitized;
            },
            senderRole() {
                return this.message.role;
            },
            senderName() {
                switch (this.senderRole) {
                    case "user":
                        return this.$t('USER_NAME');
                    case "assistant":
                        return this.$t('CHATBOT_NAME');
                    case "system":
                        return ""
                }

                return this.message.role.charAt(0).toUpperCase()+this.message.role.slice(1);
            },
            sentTime() {
                const date = new Date(this.message.sent);
                const today = new Date();

                // Create formatter for date without time
                const dateFormatter = new Intl.DateTimeFormat([], {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                });

                // Get the formatted date strings for today and yesterday
                const todayString = dateFormatter.format(today);
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                const yesterdayString = dateFormatter.format(yesterday);

                let formattedTimestamp;

                if (date.toLocaleDateString() === todayString) {
                    // If the message was sent today
                    formattedTimestamp = `Today ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
                } else if (date.toLocaleDateString() === yesterdayString) {
                    // If the message was sent yesterday
                    formattedTimestamp = `Yesterday ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
                } else {
                    // For any other day
                    formattedTimestamp = date.toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        timeZoneName: 'short',
                    });
                }

                return formattedTimestamp;
            },
        }
    };
</script>